import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { FiClock, FiDollarSign, FiTruck, FiUser } from 'react-icons/fi';
import AnalyticsService from '../../services/analyticsService';

const RecentActivity = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        const response = await AnalyticsService.getRecentActivity();
        setActivities(response.data);
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        // Fallback to demo data
        setActivities([
          {
            id: 1,
            type: 'car_added',
            title: 'New car listed',
            description: '2022 Honda City added by Seller #123',
            time: '2 minutes ago',
            icon: FiTruck,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100'
          },
          {
            id: 2,
            type: 'seller_joined',
            title: 'New seller registered',
            description: 'Rajesh Kumar joined as seller',
            time: '15 minutes ago',
            icon: FiUser,
            color: 'text-green-600',
            bgColor: 'bg-green-100'
          },
          {
            id: 3,
            type: 'sale_completed',
            title: 'Car sold',
            description: '2021 Swift sold for ₹6.5L',
            time: '1 hour ago',
            icon: FiDollarSign,
            color: 'text-orange-600',
            bgColor: 'bg-orange-100'
          },
          {
            id: 4,
            type: 'inquiry',
            title: 'Test drive booked',
            description: 'BMW X3 test drive scheduled',
            time: '2 hours ago',
            icon: FiClock,
            color: 'text-purple-600',
            bgColor: 'bg-purple-100'
          },
          {
            id: 5,
            type: 'car_added',
            title: 'Bulk upload completed',
            description: '25 cars uploaded via Excel',
            time: '3 hours ago',
            icon: FiTruck,
            color: 'text-blue-600',
            bgColor: 'bg-blue-100'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivity();
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'car_added':
      case 'car_approved':
      case 'car_rejected':
        return FiTruck;
      case 'seller_joined':
      case 'seller_verified':
        return FiUser;
      case 'sale_completed':
      case 'payment_received':
        return FiDollarSign;
      case 'inquiry':
      case 'test_drive':
        return FiClock;
      default:
        return FiClock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'car_added':
      case 'car_approved':
        return { color: 'text-blue-600', bgColor: 'bg-blue-100' };
      case 'seller_joined':
      case 'seller_verified':
        return { color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'sale_completed':
      case 'payment_received':
        return { color: 'text-orange-600', bgColor: 'bg-orange-100' };
      case 'car_rejected':
        return { color: 'text-red-600', bgColor: 'bg-red-100' };
      default:
        return { color: 'text-purple-600', bgColor: 'bg-purple-100' };
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 card-shadow">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h3>
        <div className="space-y-4">
          {[...Array(5)].map((_, index) => (
            <div key={index} className="flex items-start space-x-4 p-3">
              <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded animate-pulse mb-2"></div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 card-shadow">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h3>
      <div className="space-y-4">
        {activities.map((activity, index) => {
          const IconComponent = getActivityIcon(activity.type);
          const { color, bgColor } = getActivityColor(activity.type);
          
          return (
            <motion.div
              key={activity.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ scale: 1.02 }}
              className="flex items-start space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-colors duration-200"
            >
              <div className={`p-2 rounded-lg ${bgColor}`}>
                <IconComponent className={`h-4 w-4 ${color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm">{activity.title}</p>
                <p className="text-gray-600 text-xs mt-1">{activity.description}</p>
                <p className="text-gray-400 text-xs mt-1">{activity.time}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium py-2 text-center border-t border-gray-100"
      >
        View all activity
      </motion.button>
    </div>
  );
};

export default RecentActivity;